import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../api';
import { Dialog } from '../components/Dialog';
import { Disclosure } from '../components/Disclosure';
import { formatDateTime } from '../format';
import { PageHead } from '../Layout';
import { PreferencesPanel } from './Lessons';
import type {
  AnchorExample,
  ContentProfile,
  EditingMode,
  EditorialStage,
  Piece,
  PublicationWithProfiles,
  ResearchMode,
} from '../../shared/editorial';
import {
  buildCadenceCron,
  cadenceLabel,
  contentTypeLabel,
  inferPublicationKind,
  parseCadenceCron,
  parsePromptPreferences,
  parseSettings,
  pieceLabel,
  publicationKindLabel,
} from '../editorial/view';
import '../publications.css';

type PublicationKind = 'x_account' | 'website' | 'telegram';
type PublicationItem = PublicationWithProfiles & { kind?: PublicationKind };
type WorkspaceTab = 'content' | 'preferences' | 'settings';
type StageFilter = 'all' | EditorialStage;

type ProfileDraft = Omit<
  ContentProfile,
  | 'id'
  | 'publicationId'
  | 'version'
  | 'history'
  | 'createdAt'
  | 'updatedAt'
  | 'settings'
  | 'promptPreferences'
> & {
  editingMode: EditingMode;
  promptPreferencesText: string;
  settingsText: string;
};

interface PublicationDraft {
  kind: PublicationKind;
  name: string;
  slug: string;
  mission: string;
  audience: string;
  enabled: boolean;
}

interface PieceDraft {
  title: string;
  bodyMd: string;
}

const EMPTY_PROFILE: ProfileDraft = {
  name: 'Articles',
  format: 'article',
  destination: 'web',
  enabled: false,
  cadenceCron: null,
  timezone: 'America/Chicago',
  maxWords: 1200,
  styleGuide: '',
  selectionGuide: '',
  editingGuide: '',
  researchMode: 'light',
  anchorExamples: [],
  visualStyle: '',
  editingMode: 'surgical',
  promptPreferencesText: '',
  settingsText: '{}',
};

const EMPTY_PUBLICATION: PublicationDraft = {
  kind: 'x_account',
  name: '',
  slug: '',
  mission: '',
  audience: '',
  enabled: true,
};

const EMPTY_PIECE: PieceDraft = { title: '', bodyMd: '' };

const STAGE_FILTERS: { value: StageFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Drafts' },
  { value: 'queued', label: 'Queued' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function publicationToDraft(publication: PublicationItem): PublicationDraft {
  return {
    kind: inferPublicationKind(publication),
    name: publication.name,
    slug: publication.slug,
    mission: publication.mission,
    audience: publication.audience,
    enabled: publication.enabled,
  };
}

function profileToDraft(profile: ContentProfile): ProfileDraft {
  return {
    name: profile.name,
    format: profile.format,
    destination: profile.destination,
    enabled: profile.enabled,
    cadenceCron: profile.cadenceCron,
    timezone: profile.timezone,
    maxWords: profile.maxWords,
    styleGuide: profile.styleGuide,
    selectionGuide: profile.selectionGuide,
    editingGuide: profile.editingGuide,
    researchMode: profile.researchMode,
    anchorExamples: profile.anchorExamples,
    visualStyle: profile.visualStyle,
    editingMode: profile.settings.editingMode === 'source_only' ? 'source_only' : 'surgical',
    promptPreferencesText: profile.promptPreferences.join('\n'),
    settingsText: JSON.stringify(profile.settings, null, 2),
  };
}

function profilePayload(draft: ProfileDraft) {
  return {
    name: draft.name.trim(),
    format: draft.format,
    destination: draft.destination,
    enabled: draft.enabled,
    cadenceCron: draft.cadenceCron?.trim() || null,
    timezone: draft.timezone.trim(),
    maxWords: Number(draft.maxWords),
    styleGuide: draft.styleGuide,
    selectionGuide: draft.selectionGuide,
    editingGuide: draft.editingGuide,
    researchMode: draft.researchMode,
    anchorExamples: draft.anchorExamples,
    visualStyle: draft.visualStyle,
    promptPreferences: parsePromptPreferences(draft.promptPreferencesText),
    settings: { ...parseSettings(draft.settingsText), editingMode: draft.editingMode },
  };
}

function CadenceControl({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (next: string | null) => void;
}) {
  const fields = parseCadenceCron(value);
  return (
    <fieldset className="pub-cadence">
      <legend>Publishing cadence</legend>
      <div className="pub-cadence-grid">
        <label className="field">
          <span>Frequency</span>
          <select
            value={fields.frequency}
            onChange={(event) => {
              const frequency = event.target.value as 'none' | 'daily' | 'weekly';
              onChange(buildCadenceCron(frequency, fields.time, fields.weekday));
            }}
          >
            <option value="none">No automatic cadence</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            {fields.frequency === 'custom' ? <option value="custom" disabled>Custom</option> : null}
          </select>
        </label>
        {fields.frequency === 'daily' || fields.frequency === 'weekly' ? (
          <label className="field">
            <span>Time</span>
            <input
              type="time"
              value={fields.time}
              onChange={(event) =>
                onChange(
                  buildCadenceCron(
                    fields.frequency === 'weekly' ? 'weekly' : 'daily',
                    event.target.value,
                    fields.weekday,
                  ),
                )
              }
            />
          </label>
        ) : null}
        {fields.frequency === 'weekly' ? (
          <label className="field">
            <span>Day</span>
            <select value={fields.weekday} onChange={(event) => onChange(buildCadenceCron('weekly', fields.time, event.target.value))}>
              <option value="1">Monday</option><option value="2">Tuesday</option><option value="3">Wednesday</option><option value="4">Thursday</option><option value="5">Friday</option><option value="6">Saturday</option><option value="0">Sunday</option>
            </select>
          </label>
        ) : null}
      </div>
      <Disclosure title="Advanced schedule" className="pub-inline-disclosure">
        <label className="field">
          <span>Schedule expression</span>
          <input className="mono" value={value ?? ''} placeholder="0 9 * * 1" onChange={(event) => onChange(event.target.value || null)} />
          <span className="hint">Use a five-part schedule only when the presets do not fit.</span>
        </label>
      </Disclosure>
    </fieldset>
  );
}

function ProfileForm({
  draft,
  setDraft,
  saving,
  onSave,
  saveLabel,
  publicationKind,
  compact = false,
}: {
  draft: ProfileDraft;
  setDraft: (next: ProfileDraft) => void;
  saving: boolean;
  onSave: () => void;
  saveLabel: string;
  publicationKind: PublicationKind;
  compact?: boolean;
}) {
  function changeAnchor(index: number, patch: Partial<AnchorExample>) {
    setDraft({
      ...draft,
      anchorExamples: draft.anchorExamples.map((example, i) => i === index ? { ...example, ...patch } : example),
    });
  }

  return (
    <form className={`pub-settings-form${compact ? ' pub-settings-form-compact' : ''}`} onSubmit={(event) => { event.preventDefault(); onSave(); }}>
      <section className="pub-settings-section">
        <div className="pub-section-heading"><div><h3>Content type</h3><p>Its destination, length, and operating state.</p></div></div>
        <div className="pub-form-grid">
          <label className="field"><span>Name</span><input required value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></label>
          <div className="field"><span>Format</span><div className="pub-inherited-value">{contentTypeLabel(draft.format)}</div></div>
          <div className="field"><span>Destination</span><div className="pub-inherited-value">{publicationKind === 'x_account' ? 'Connected X account' : publicationKind === 'website' ? 'Website magazine' : 'Telegram'}</div></div>
          {draft.format === 'tweet' ? <div className="field"><span>Length</span><div className="pub-inherited-value">280 character limit</div></div> : <label className="field"><span>Maximum words</span><input required min={1} type="number" value={draft.maxWords} onChange={(event) => setDraft({ ...draft, maxWords: Number(event.target.value) })} /></label>}
          <label className="field"><span>Timezone</span><input required value={draft.timezone} onChange={(event) => setDraft({ ...draft, timezone: event.target.value })} /></label>
          <label className="pub-switch"><input type="checkbox" checked={draft.enabled} onChange={(event) => setDraft({ ...draft, enabled: event.target.checked })} /><span>Automation enabled</span></label>
        </div>
      </section>

      <CadenceControl value={draft.cadenceCron} onChange={(cadenceCron) => setDraft({ ...draft, cadenceCron })} />

      {!compact ? (
        <>
          <section className="pub-settings-section">
            <div className="pub-section-heading"><div><h3>Editorial direction</h3><p>Stable guidance used when work is selected and developed.</p></div></div>
            <div className="pub-form-grid">
              <label className="field"><span>Research depth</span><select value={draft.researchMode} onChange={(event) => setDraft({ ...draft, researchMode: event.target.value as ResearchMode })}><option value="none">None</option><option value="light">Light</option><option value="deep">Deep</option></select></label>
              <label className="field"><span>Editing mode</span><select value={draft.editingMode} onChange={(event) => setDraft({ ...draft, editingMode: event.target.value as EditingMode })}><option value="surgical">Surgical edits</option><option value="source_only">Source words only</option></select></label>
            </div>
            <label className="field"><span>Selection guide</span><textarea rows={4} value={draft.selectionGuide} onChange={(event) => setDraft({ ...draft, selectionGuide: event.target.value })} /></label>
            <label className="field"><span>Style guide</span><textarea rows={5} value={draft.styleGuide} onChange={(event) => setDraft({ ...draft, styleGuide: event.target.value })} /></label>
            <label className="field"><span>Editing guide</span><textarea rows={4} value={draft.editingGuide} onChange={(event) => setDraft({ ...draft, editingGuide: event.target.value })} /></label>
            <label className="field"><span>Visual direction</span><textarea rows={3} value={draft.visualStyle} onChange={(event) => setDraft({ ...draft, visualStyle: event.target.value })} /></label>
            <label className="field"><span>Prompt preferences</span><textarea rows={4} value={draft.promptPreferencesText} placeholder="One preference per line" onChange={(event) => setDraft({ ...draft, promptPreferencesText: event.target.value })} /></label>
          </section>

          <Disclosure title="Reference examples" meta={`${draft.anchorExamples.length} saved`} className="pub-settings-disclosure">
            {draft.anchorExamples.length === 0 ? <p className="muted">No reference examples yet.</p> : null}
            {draft.anchorExamples.map((example, index) => (
              <div className="pub-example-editor" key={index}>
                <label className="field"><span>Title</span><input value={example.title ?? ''} onChange={(event) => changeAnchor(index, { title: event.target.value || undefined })} /></label>
                <label className="field"><span>Body</span><textarea required rows={5} value={example.body} onChange={(event) => changeAnchor(index, { body: event.target.value })} /></label>
                <label className="field"><span>Why it matters</span><input value={example.note ?? ''} onChange={(event) => changeAnchor(index, { note: event.target.value || undefined })} /></label>
                <button type="button" onClick={() => setDraft({ ...draft, anchorExamples: draft.anchorExamples.filter((_, i) => i !== index) })}>Remove example</button>
              </div>
            ))}
            <button type="button" onClick={() => setDraft({ ...draft, anchorExamples: [...draft.anchorExamples, { body: '' }] })}>Add example</button>
          </Disclosure>

          <Disclosure title="Advanced bounds" className="pub-settings-disclosure">
            <label className="field"><span>Settings JSON</span><textarea className="mono" rows={8} value={draft.settingsText} onChange={(event) => setDraft({ ...draft, settingsText: event.target.value })} /></label>
          </Disclosure>
        </>
      ) : null}

      <div className="pub-form-actions"><button className="primary" disabled={saving}>{saving ? 'Saving…' : saveLabel}</button></div>
    </form>
  );
}

function PublicationForm({ draft, setDraft, includeKind }: { draft: PublicationDraft; setDraft: (next: PublicationDraft) => void; includeKind: boolean }) {
  return (
    <div className="pub-dialog-form">
      {includeKind ? (
        <fieldset className="pub-type-choice">
          <legend>Publication type</legend>
          <label className={draft.kind === 'x_account' ? 'active' : ''}>
            <input type="radio" name="publication-kind" checked={draft.kind === 'x_account'} onChange={() => setDraft({ ...draft, kind: 'x_account' })} />
            <strong>X account</strong><span>Tweets and articles using the connected X account.</span>
          </label>
          <label className={draft.kind === 'website' ? 'active' : ''}>
            <input type="radio" name="publication-kind" checked={draft.kind === 'website'} onChange={() => setDraft({ ...draft, kind: 'website' })} />
            <strong>Website magazine</strong><span>A named magazine with articles on the web.</span>
          </label>
        </fieldset>
      ) : null}
      {draft.kind === 'x_account' ? <p className="pub-connected-note">Uses the connected X account. Creating this publication does not connect another identity or schedule posts.</p> : null}
      <label className="field">
        <span>Name</span>
        <input required value={draft.name} placeholder={draft.kind === 'website' ? 'Tentacle Technical Journal' : 'Tentacle'} onChange={(event) => { const name = event.target.value; const previousAutoSlug = slugify(draft.name); setDraft({ ...draft, name, slug: !draft.slug || draft.slug === previousAutoSlug ? slugify(name) : draft.slug }); }} />
      </label>
      <label className="field"><span>URL slug</span><input required value={draft.slug} placeholder={draft.kind === 'website' ? 'tentacle-technical-journal' : 'tentacle'} onChange={(event) => setDraft({ ...draft, slug: slugify(event.target.value) })} /></label>
      <label className="field"><span>Mission</span><textarea rows={3} value={draft.mission} placeholder="What this publication exists to do" onChange={(event) => setDraft({ ...draft, mission: event.target.value })} /></label>
      <label className="field"><span>Audience</span><textarea rows={2} value={draft.audience} placeholder="Who it is for" onChange={(event) => setDraft({ ...draft, audience: event.target.value })} /></label>
      {!includeKind ? <label className="pub-switch"><input type="checkbox" checked={draft.enabled} onChange={(event) => setDraft({ ...draft, enabled: event.target.checked })} /><span>Active publication</span></label> : null}
    </div>
  );
}

export default function Publications() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [publications, setPublications] = useState<PublicationItem[]>([]);
  const [pieces, setPieces] = useState<Piece[]>([]);
  const [loading, setLoading] = useState(true);
  const [piecesLoading, setPiecesLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [stageFilter, setStageFilter] = useState<StageFilter>('all');
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [profileDraft, setProfileDraft] = useState<ProfileDraft>(EMPTY_PROFILE);
  const [publicationDraft, setPublicationDraft] = useState<PublicationDraft>({ ...EMPTY_PUBLICATION });
  const [pieceDraft, setPieceDraft] = useState<PieceDraft>({ ...EMPTY_PIECE });
  const [modal, setModal] = useState<'new-publication' | 'edit-publication' | 'new-profile' | 'new-piece' | null>(null);
  const piecesRequestRef = useRef(0);

  const requestedPublicationId = Number(searchParams.get('publicationId') ?? searchParams.get('publication')) || null;
  const requestedProfileId = Number(searchParams.get('profileId') ?? searchParams.get('profile')) || null;
  const requestedTab = searchParams.get('tab');
  const activeTab: WorkspaceTab = requestedTab === 'preferences' || requestedTab === 'settings' ? requestedTab : 'content';
  const requestedProfileOwner = requestedProfileId
    ? publications.find((item) => item.profiles.some((candidate) => candidate.id === requestedProfileId))
    : null;
  const publication = publications.find((item) => item.id === requestedPublicationId) ?? requestedProfileOwner ?? publications[0] ?? null;
  const orderedProfiles = useMemo(() => {
    if (!publication) return [];
    const rank = (item: ContentProfile) => item.format === 'tweet' ? 0 : item.format === 'article' ? 1 : 2;
    return [...publication.profiles].sort((a, b) => rank(a) - rank(b) || a.id - b.id);
  }, [publication]);
  const profile = orderedProfiles.find((item) => item.id === requestedProfileId) ?? orderedProfiles[0] ?? null;

  const loadPublications = useCallback(async () => {
    const response = await api.get<{ items: PublicationItem[] }>('/editorial/publications');
    setPublications(response.items);
    return response.items;
  }, []);

  useEffect(() => {
    void loadPublications().catch((err) => setError(err instanceof Error ? err.message : String(err))).finally(() => setLoading(false));
  }, [loadPublications]);

  useEffect(() => {
    if (!publication) return;
    setPublicationDraft(publicationToDraft(publication));
  }, [publication?.id, publication?.updatedAt]);

  useEffect(() => { if (profile) setProfileDraft(profileToDraft(profile)); }, [profile?.id, profile?.version]);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearchQuery(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  const loadPieces = useCallback(async (cursor?: string) => {
    if (!profile) { setPieces([]); setNextCursor(null); return; }
    const requestId = ++piecesRequestRef.current;
    setLoadingMore(Boolean(cursor));
    try {
      const query = new URLSearchParams({ profileId: String(profile.id), limit: '100' });
      if (stageFilter !== 'all') query.set('stage', stageFilter);
      if (searchQuery) query.set('q', searchQuery);
      if (cursor) query.set('cursor', cursor);
      const response = await api.get<{ items: Piece[]; nextCursor: string | null }>(`/editorial/pieces?${query}`);
      if (requestId !== piecesRequestRef.current) return;
      setPieces((current) => cursor ? [...current, ...response.items] : response.items);
      setNextCursor(response.nextCursor);
    } catch (err) {
      if (requestId === piecesRequestRef.current) setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (requestId === piecesRequestRef.current) setLoadingMore(false);
    }
  }, [profile?.id, searchQuery, stageFilter]);

  useEffect(() => {
    if (!profile) { setPieces([]); setNextCursor(null); return; }
    let cancelled = false;
    const requestId = ++piecesRequestRef.current;
    setPieces([]);
    setNextCursor(null);
    setLoadingMore(false);
    setPiecesLoading(true);
    const query = new URLSearchParams({ profileId: String(profile.id), limit: '100' });
    if (stageFilter !== 'all') query.set('stage', stageFilter);
    if (searchQuery) query.set('q', searchQuery);
    api.get<{ items: Piece[]; nextCursor: string | null }>(`/editorial/pieces?${query}`).then((response) => { if (!cancelled && requestId === piecesRequestRef.current) { setPieces(response.items); setNextCursor(response.nextCursor); } }).catch((err) => { if (!cancelled && requestId === piecesRequestRef.current) setError(err instanceof Error ? err.message : String(err)); }).finally(() => { if (!cancelled && requestId === piecesRequestRef.current) setPiecesLoading(false); });
    return () => { cancelled = true; };
  }, [profile?.id, searchQuery, stageFilter]);

  const visiblePieces = pieces;

  function updateRoute(patch: { publicationId?: number; profileId?: number | null; tab?: WorkspaceTab }) {
    const next = new URLSearchParams(searchParams);
    next.delete('publication'); next.delete('profile');
    if (patch.publicationId) next.set('publicationId', String(patch.publicationId));
    if (patch.profileId === null) next.delete('profileId'); else if (patch.profileId) next.set('profileId', String(patch.profileId));
    if (patch.tab) next.set('tab', patch.tab);
    setSearchParams(next);
  }

  function selectPublication(id: number) { if (id === publication?.id) return; piecesRequestRef.current += 1; updateRoute({ publicationId: id, profileId: null, tab: 'content' }); setStageFilter('all'); setSearch(''); setSearchQuery(''); }
  function selectProfile(id: number) { if (id === profile?.id) return; piecesRequestRef.current += 1; if (publication) updateRoute({ publicationId: publication.id, profileId: id, tab: activeTab }); setStageFilter('all'); setSearch(''); setSearchQuery(''); }

  function closeModal() {
    setModal(null);
    setError(null);
    if (publication) setPublicationDraft(publicationToDraft(publication));
    if (profile) setProfileDraft(profileToDraft(profile));
    setPieceDraft({ ...EMPTY_PIECE });
  }

  async function savePublication() {
    if (!publication) return;
    setSaving(true); setError(null);
    try {
      await api.patch(`/editorial/publications/${publication.id}`, { name: publicationDraft.name, slug: publicationDraft.slug, mission: publicationDraft.mission, audience: publicationDraft.audience, enabled: publicationDraft.enabled });
      await loadPublications(); setModal(null); setNotice('Publication updated.');
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); } finally { setSaving(false); }
  }

  async function createPublication() {
    setSaving(true); setError(null);
    try {
      const response = await api.post<{ publication: { id: number } }>('/editorial/publications', publicationDraft);
      await loadPublications(); setModal(null);
      updateRoute({ publicationId: response.publication.id, profileId: null, tab: 'content' });
      setNotice('Publication created. Its content types are paused until you enable and schedule them.');
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); } finally { setSaving(false); }
  }

  async function saveProfile(create: boolean) {
    if (!publication) return;
    setSaving(true); setError(null);
    try {
      const payload = profilePayload(profileDraft);
      const response = create ? await api.post<{ profile: ContentProfile }>('/editorial/profiles', { publicationId: publication.id, ...payload }) : await api.patch<{ profile: ContentProfile }>(`/editorial/profiles/${profile!.id}`, { expectedVersion: profile!.version, ...payload });
      await loadPublications(); setModal(null);
      updateRoute({ publicationId: publication.id, profileId: response.profile.id, tab: create ? 'content' : 'settings' });
      setNotice(create ? 'Content type created.' : 'Content type settings saved.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setError('These settings changed elsewhere. Reload before saving again.'); else setError(err instanceof Error ? err.message : String(err));
    } finally { setSaving(false); }
  }

  async function createPiece() {
    if (!profile) return;
    setSaving(true); setError(null);
    try {
      const response = await api.post<{ piece: Piece }>('/editorial/pieces', { profileId: profile.id, title: pieceDraft.title.trim() || null, bodyMd: pieceDraft.bodyMd, sourceRefs: [], rationale: 'Started in the editorial workspace.' });
      setModal(null); navigate(`/pieces/${response.piece.id}`);
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); } finally { setSaving(false); }
  }

  if (loading) return <div className="empty">Loading publications…</div>;
  const kind = publication ? inferPublicationKind(publication) : null;

  return (
    <>
      <PageHead title="Publications" subtitle="Manage each destination, its content types, and the editorial system behind them." actions={<button className="primary" onClick={() => { setError(null); setPublicationDraft({ ...EMPTY_PUBLICATION }); setModal('new-publication'); }}>New publication</button>} />
      {error ? <p className="editorial-alert fail" role="alert">{error}</p> : null}
      {notice ? <p className="editorial-alert" role="status">{notice}</p> : null}

      {publications.length === 0 ? (
        <section className="pub-empty"><div className="pub-empty-mark">P</div><h2>Create your first publication</h2><p>An X account starts with Tweets and Articles. A website magazine starts with Articles.</p><button className="primary" onClick={() => { setError(null); setPublicationDraft({ ...EMPTY_PUBLICATION }); setModal('new-publication'); }}>New publication</button></section>
      ) : (
        <div className="pub-layout">
          <aside className="pub-rail" aria-label="Publications">
            <div className="pub-rail-label">Publications</div>
            {publications.map((item) => {
              const itemKind = inferPublicationKind(item);
              const total = Object.values(item.counts).reduce((sum, count) => sum + count, 0);
              return <button type="button" key={item.id} className={item.id === publication?.id ? 'active' : ''} onClick={() => selectPublication(item.id)} aria-current={item.id === publication?.id ? 'page' : undefined}><span className={`pub-kind-mark pub-kind-${itemKind}`} aria-hidden="true">{itemKind === 'x_account' ? 'X' : itemKind === 'website' ? 'W' : 'T'}</span><span className="pub-rail-copy"><strong>{item.name}</strong><small>{publicationKindLabel(itemKind)} · {total} {total === 1 ? 'piece' : 'pieces'}</small></span></button>;
            })}
          </aside>

          {publication ? (
            <div className="pub-main">
              <header className="pub-header">
                <div><div className="pub-title-line"><h2>{publication.name}</h2><span className={`pub-status ${publication.enabled ? 'active' : 'paused'}`}>{publication.enabled ? 'Active' : 'Paused'}</span></div><p className="pub-type-label">{publicationKindLabel(kind!)}</p>{publication.mission ? <p className="pub-mission">{publication.mission}</p> : null}</div>
                <div className="pub-header-actions"><button onClick={() => { setError(null); setPublicationDraft(publicationToDraft(publication)); setModal('edit-publication'); }}>Publication settings</button></div>
              </header>

              <nav className="pub-profile-tabs" aria-label={`Content types in ${publication.name}`}>
                {orderedProfiles.map((item) => <button type="button" key={item.id} className={item.id === profile?.id ? 'active' : ''} aria-pressed={item.id === profile?.id} onClick={() => selectProfile(item.id)}><span>{contentTypeLabel(item.format)}</span><small>{item.enabled ? cadenceLabel(item.cadenceCron, item.timezone) : 'Paused'}</small></button>)}
                {kind === 'x_account' && !orderedProfiles.some((item) => item.format === 'tweet') ? <button type="button" className="pub-add-type" onClick={() => { setError(null); setProfileDraft({ ...EMPTY_PROFILE, name: 'Tweets', format: 'tweet', destination: 'x', maxWords: 280 }); setModal('new-profile'); }}><span>Add Tweets</span><small>Paused by default</small></button> : null}
                {kind !== 'telegram' && !orderedProfiles.some((item) => item.format === 'article') ? <button type="button" className="pub-add-type" onClick={() => { setError(null); setProfileDraft({ ...EMPTY_PROFILE, name: 'Articles', format: 'article', destination: kind === 'x_account' ? 'x' : 'web' }); setModal('new-profile'); }}><span>Add Articles</span><small>Paused by default</small></button> : null}
              </nav>

              {profile ? (
                <>
                  <nav className="pub-subnav" aria-label={`${contentTypeLabel(profile.format)} sections`}>
                    {(['content', 'preferences', 'settings'] as const).map((tab) => <button type="button" key={tab} aria-pressed={activeTab === tab} onClick={() => updateRoute({ publicationId: publication.id, profileId: profile.id, tab })}>{tab[0].toUpperCase() + tab.slice(1)}</button>)}
                  </nav>

                  {activeTab === 'content' ? (
                    <section className="pub-content">
                      <div className="pub-content-toolbar">
                        <div className="pub-stage-filters" aria-label="Filter by stage">
                          {STAGE_FILTERS.map((option) => <button type="button" key={option.value} className={stageFilter === option.value ? 'active' : ''} aria-pressed={stageFilter === option.value} onClick={() => { if (option.value === stageFilter) return; piecesRequestRef.current += 1; setStageFilter(option.value); }}>{option.label}</button>)}
                        </div>
                        <div className="pub-content-actions"><label className="pub-search"><span className="sr-only">Search pieces</span><input type="search" value={search} placeholder="Search all content" onChange={(event) => setSearch(event.target.value)} /></label><button className="primary" onClick={() => { setError(null); setPieceDraft({ ...EMPTY_PIECE }); setModal('new-piece'); }}>New {profile.format === 'tweet' ? 'tweet' : 'article'}</button></div>
                      </div>

                      {piecesLoading ? <div className="pub-table-empty">Loading content…</div> : visiblePieces.length === 0 ? (
                        <div className="pub-table-empty"><strong>No content in this view</strong><span>{pieces.length === 0 ? 'Create the first piece or let discovery work in the background.' : 'Try another stage or search.'}</span></div>
                      ) : (
                        <><div className="pub-piece-table-wrap"><table className="pub-piece-table"><thead><tr><th>Piece</th><th>Stage</th><th>Delivery</th><th>Timing</th><th aria-label="Open" /></tr></thead><tbody>{visiblePieces.map((item) => <tr key={item.id}><td><Link to={`/pieces/${item.id}`}><strong>{pieceLabel(item)}</strong><span>{item.bodyMd ? profile.format === 'tweet' ? `${item.bodyMd.length} characters` : `${item.bodyMd.trim().split(/\s+/).filter(Boolean).length} words` : 'Empty draft'}</span></Link></td><td><span className={`pub-stage pub-stage-${item.editorialStage}`}>{item.editorialStage}</span></td><td><span className={item.deliveryStatus === 'failed' ? 'pub-delivery-failed' : 'pub-delivery'}>{item.deliveryStatus}</span></td><td><span className="pub-date">{item.scheduledFor ? formatDateTime(item.scheduledFor) : formatDateTime(item.updatedAt)}</span></td><td><Link className="pub-open" aria-label={`Open ${pieceLabel(item)}`} to={`/pieces/${item.id}`}>Open</Link></td></tr>)}</tbody></table></div>{nextCursor ? <div className="pub-load-more"><span>{pieces.length} loaded</span><button disabled={loadingMore} onClick={() => void loadPieces(nextCursor)}>{loadingMore ? 'Loading…' : 'Load more'}</button></div> : <div className="pub-load-more"><span>{pieces.length} loaded</span></div>}</>
                      )}
                    </section>
                  ) : null}
                  {activeTab === 'preferences' ? <PreferencesPanel key={profile.id} profileId={profile.id} /> : null}
                  {activeTab === 'settings' ? <section className="pub-settings"><div className="pub-settings-intro"><div><h2>{contentTypeLabel(profile.format)} settings</h2><p>Cadence, editorial direction, and boundaries for this content type.</p></div><span>Version {profile.version}</span></div><ProfileForm draft={profileDraft} setDraft={setProfileDraft} saving={saving} onSave={() => void saveProfile(false)} saveLabel="Save settings" publicationKind={kind!} /></section> : null}
                </>
              ) : <div className="pub-table-empty"><strong>No content types</strong><span>This publication needs a supported content type.</span></div>}
            </div>
          ) : null}
        </div>
      )}

      {modal === 'new-publication' ? <Dialog title="New publication" onClose={closeModal} footer={<><button onClick={closeModal}>Cancel</button><button className="primary" disabled={saving || !publicationDraft.name.trim() || !publicationDraft.slug} onClick={() => void createPublication()}>{saving ? 'Creating…' : 'Create publication'}</button></>}>{error ? <p className="editorial-alert fail" role="alert">{error}</p> : null}<PublicationForm draft={publicationDraft} setDraft={setPublicationDraft} includeKind /></Dialog> : null}
      {modal === 'edit-publication' && publication ? <Dialog title="Publication settings" onClose={closeModal} footer={<><button onClick={closeModal}>Cancel</button><button className="primary" disabled={saving || !publicationDraft.name.trim() || !publicationDraft.slug} onClick={() => void savePublication()}>{saving ? 'Saving…' : 'Save changes'}</button></>}>{error ? <p className="editorial-alert fail" role="alert">{error}</p> : null}<p className="pub-dialog-context">{publicationKindLabel(kind!)} · changes apply across its content types.</p><PublicationForm draft={publicationDraft} setDraft={setPublicationDraft} includeKind={false} /></Dialog> : null}
      {modal === 'new-profile' && publication ? <Dialog title="Add content type" onClose={closeModal} footer={<button onClick={closeModal}>Cancel</button>}>{error ? <p className="editorial-alert fail" role="alert">{error}</p> : null}<p className="pub-dialog-context">Add another editorial line to {publication.name}.</p><ProfileForm draft={profileDraft} setDraft={setProfileDraft} saving={saving} onSave={() => void saveProfile(true)} saveLabel="Add content type" publicationKind={kind!} compact /></Dialog> : null}
      {modal === 'new-piece' && profile ? <Dialog title={`New ${profile.format === 'tweet' ? 'tweet' : 'article'}`} onClose={closeModal} footer={<><button onClick={closeModal}>Cancel</button><button className="primary" disabled={saving} onClick={() => void createPiece()}>{saving ? 'Creating…' : 'Create draft'}</button></>}>{error ? <p className="editorial-alert fail" role="alert">{error}</p> : null}<p className="pub-dialog-context">Start a draft in {publication?.name} · {contentTypeLabel(profile.format)}.</p>{profile.format === 'article' ? <label className="field"><span>Working title</span><input autoFocus value={pieceDraft.title} placeholder="Untitled is fine" onChange={(event) => setPieceDraft({ ...pieceDraft, title: event.target.value })} /></label> : null}<label className="field"><span>{profile.format === 'tweet' ? 'Tweet' : 'Opening draft'}</span><textarea autoFocus={profile.format === 'tweet'} rows={profile.format === 'tweet' ? 6 : 9} value={pieceDraft.bodyMd} placeholder="You can leave this empty and develop it in the editor." onChange={(event) => setPieceDraft({ ...pieceDraft, bodyMd: event.target.value })} /></label></Dialog> : null}
    </>
  );
}
